import { DispatchStop } from './dispatch-planner.component';
import { parseCustomerNameFromAddress } from './dispatch-planner-stop-format';

export interface MarkerInfoLabelParts {
  orderNumber: string;
  nachname: string;
  serviceLabel: string;
}

export function getStopNachname(stop: DispatchStop): string {
  if (stop.nachname?.trim()) {
    return stop.nachname.trim();
  }
  const name = (stop.customerName || '').trim();
  if (name) {
    const parts = name.split(/\s+/);
    return parts.length > 1 ? parts[parts.length - 1] : parts[0];
  }
  if (stop.source === 'delivery-suggestion') {
    const fromRechnung = parseCustomerNameFromAddress(stop.rechnungsadresse);
    if (fromRechnung.nachname) {
      return fromRechnung.nachname;
    }
    const fromLieferung = parseCustomerNameFromAddress(stop.address);
    if (fromLieferung.nachname) {
      return fromLieferung.nachname;
    }
  }
  return '';
}

export function getStopServiceTypeLabel(stop: DispatchStop): string {
  const hasMontage = stopHasMontage(stop);
  const hasLieferung = stopHasLieferung(stop);
  if (hasMontage && hasLieferung) {
    return 'LiMo';
  }
  if (hasMontage) {
    return 'Mo';
  }
  if (hasLieferung) {
    return 'Li';
  }
  return '';
}

export function getStopServiceTypeFullLabel(stop: DispatchStop): string {
  const hasMontage = stopHasMontage(stop);
  const hasLieferung = stopHasLieferung(stop);
  if (hasMontage && hasLieferung) {
    return 'Lieferung + Montage';
  }
  if (hasMontage) {
    return 'Montage';
  }
  if (hasLieferung) {
    return 'Lieferung';
  }
  return '';
}

export function buildMarkerInfoLabelParts(stop: DispatchStop): MarkerInfoLabelParts {
  const serviceLabel = getStopServiceTypeLabel(stop);
  const me = stop.montagemenge ?? 0;
  const serviceWithMe =
    serviceLabel && stopHasMontage(stop) && me > 0 ? `${serviceLabel} ${me} ME` : serviceLabel;

  return {
    orderNumber: (stop.kaufvertragsnummer || '').trim(),
    nachname: getStopNachname(stop),
    serviceLabel: serviceWithMe
  };
}

export function getStopPrimaryArticleLabel(stop: DispatchStop): string {
  const labels = (stop.articles || [])
    .map(article => (article.bezeichnung || '').trim())
    .filter(Boolean);
  if (!labels.length) {
    return '';
  }
  if (labels.length === 1) {
    return labels[0];
  }
  return labels.join(', ');
}

/** Hover-Tooltip für Karten-Marker (title-Attribut). */
export function buildMarkerTooltipText(stop: DispatchStop): string {
  const parts = buildMarkerInfoLabelParts(stop);
  const serviceFull = getStopServiceTypeFullLabel(stop);
  const me = stop.montagemenge ?? 0;
  const serviceLine = serviceFull
    ? (stopHasMontage(stop) && me > 0 ? `${serviceFull} (${me} ME)` : serviceFull)
    : parts.serviceLabel;
  const article = getStopPrimaryArticleLabel(stop);

  const lines: string[] = [];
  if (parts.orderNumber) {
    lines.push(parts.orderNumber);
  }
  if (parts.nachname) {
    lines.push(parts.nachname);
  }
  if (serviceLine) {
    lines.push(article ? `${serviceLine}: ${article}` : serviceLine);
  } else if (article) {
    lines.push(article);
  }

  if (!lines.length) {
    return (stop.customerName || stop.kaufvertragsnummer || stop.address || '').trim();
  }

  return lines.join('\n');
}

export function buildMarkerInfoLabelHtml(stop: DispatchStop): string {
  const parts = buildMarkerInfoLabelParts(stop);
  const serviceFull = getStopServiceTypeFullLabel(stop);
  const me = stop.montagemenge ?? 0;
  const serviceLine = serviceFull
    ? (stopHasMontage(stop) && me > 0 ? `${serviceFull} (${me} ME)` : serviceFull)
    : parts.serviceLabel;
  const article = getStopPrimaryArticleLabel(stop);
  const lines: string[] = [];
  if (parts.orderNumber) {
    lines.push(`<div class="marker-info-line marker-info-kv">${escapeHtml(parts.orderNumber)}</div>`);
  }
  if (parts.nachname) {
    lines.push(`<div class="marker-info-line marker-info-name">${escapeHtml(parts.nachname)}</div>`);
  }
  if (serviceLine) {
    lines.push(`<div class="marker-info-line marker-info-type">${escapeHtml(serviceLine)}</div>`);
  }
  if (article) {
    lines.push(`<div class="marker-info-line marker-info-article">${escapeHtml(article)}</div>`);
  }
  if (!lines.length) {
    return '';
  }
  return lines.join('');
}

export function createMarkerInfoOverlay(
  position: { lat: number; lng: number },
  html: string
): google.maps.OverlayView {
  const overlay = new google.maps.OverlayView();
  let div: HTMLDivElement | null = null;

  overlay.onAdd = function onAdd(): void {
    div = document.createElement('div');
    div.className = 'dispatch-marker-info-label';
    div.innerHTML = html;
    overlay.getPanes()?.overlayMouseTarget.appendChild(div);
  };

  overlay.draw = function draw(): void {
    if (!div) {
      return;
    }
    const projection = overlay.getProjection();
    if (!projection) {
      return;
    }
    const point = projection.fromLatLngToDivPixel(new google.maps.LatLng(position.lat, position.lng));
    if (!point) {
      return;
    }
    div.style.left = `${point.x + 18}px`;
    div.style.top = `${point.y - 12}px`;
  };

  overlay.onRemove = function onRemove(): void {
    div?.remove();
    div = null;
  };

  return overlay;
}

function stopHasMontage(stop: DispatchStop): boolean {
  if ((stop.montageFlag || '').trim().toUpperCase() === 'J') {
    return true;
  }
  if ((stop.montagemenge ?? 0) > 0) {
    return true;
  }
  if ((stop.montagekostenEK ?? 0) > 0) {
    return true;
  }
  return false;
}

function stopHasLieferung(stop: DispatchStop): boolean {
  if ((stop.lieferkostenEK ?? 0) > 0) {
    return true;
  }
  if ((stop.liefermenge ?? 0) > 0) {
    return true;
  }
  if (stop.source === 'delivery-suggestion') {
    return (stop.montageFlag || '').trim().toUpperCase() !== 'J';
  }
  return false;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
